import { useState, useEffect } from 'react';
import { reportsApi, casesApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

const reportTypes = ['inspection', 'damage_assessment', 'condition_survey', 'repair_costing'];
const reportStatuses = ['draft', 'pending_review', 'approved', 'sent'];

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [cases, setCases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [formData, setFormData] = useState({
    case_id: '',
    report_type: 'inspection',
    title: '',
    content: '',
    findings: '',
    recommendations: '',
    status: 'draft',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reportsData, casesData] = await Promise.all([
        reportsApi.getAll(),
        casesApi.getAll(),
      ]);
      setReports(reportsData);
      setCases(casesData);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ case_id: '', report_type: 'inspection', title: '', content: '', findings: '', recommendations: '', status: 'draft' });
    setEditingReport(null);
  };

  const openDialog = (report = null) => {
    if (report) {
      setEditingReport(report);
      setFormData({
        case_id: report.case_id || '',
        report_type: report.report_type || 'inspection',
        title: report.title || '',
        content: report.content || '',
        findings: report.findings || '',
        recommendations: report.recommendations || '',
        status: report.status || 'draft',
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingReport) {
        await reportsApi.update(editingReport.id, formData);
        toast.success('Report updated');
      } else {
        await reportsApi.create(formData);
        toast.success('Report created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save report');
    }
  };

  const handleDelete = async (report) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await reportsApi.delete(report.id);
      toast.success('Report deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const getCaseNumber = (caseId) => {
    const caseItem = cases.find(c => c.id === caseId);
    return caseItem?.case_number || '-';
  };

  const columns = [
    { key: 'report_number', label: 'Report #' },
    { key: 'title', label: 'Title' },
    { key: 'case_id', label: 'Case', render: (val) => getCaseNumber(val) },
    { key: 'report_type', label: 'Type', render: (val) => <span className="capitalize">{val?.replace('_', ' ')}</span> },
    { key: 'status', label: 'Status', render: (val) => <StatusBadge status={val} /> },
    { key: 'created_at', label: 'Created', render: (val) => val ? format(new Date(val), 'MMM d, yyyy') : '-' },
  ];

  const actions = [
    { label: 'Edit', icon: Pencil, onClick: openDialog },
    { label: 'Delete', icon: Trash2, onClick: handleDelete, destructive: true },
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="text-sm text-stone-500 mt-1">Manage inspection and survey reports</p>
        </div>
        <Button onClick={() => openDialog()} className="bg-emerald-900 hover:bg-emerald-800" data-testid="add-report-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Report
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={reports}
        actions={actions}
        isLoading={isLoading}
        emptyMessage="No reports found. Create your first report!"
      />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle>{editingReport ? 'Edit Report' : 'Create New Report'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="case">Linked Case *</Label>
                <Select value={formData.case_id} onValueChange={(val) => setFormData({ ...formData, case_id: val })}>
                  <SelectTrigger data-testid="report-case-select">
                    <SelectValue placeholder="Select case" />
                  </SelectTrigger>
                  <SelectContent>
                    {cases.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.case_number} - {c.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="report_type">Report Type</Label>
                <Select value={formData.report_type} onValueChange={(val) => setFormData({ ...formData, report_type: val })}>
                  <SelectTrigger data-testid="report-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {reportTypes.map((t) => (
                      <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="report-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                  <SelectTrigger data-testid="report-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {reportStatuses.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="content">Content</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={3}
                data-testid="report-content-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="findings">Findings</Label>
              <Textarea
                id="findings"
                value={formData.findings}
                onChange={(e) => setFormData({ ...formData, findings: e.target.value })}
                rows={3}
                data-testid="report-findings-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="recommendations">Recommendations</Label>
              <Textarea
                id="recommendations"
                value={formData.recommendations}
                onChange={(e) => setFormData({ ...formData, recommendations: e.target.value })}
                rows={3}
                data-testid="report-recommendations-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800" data-testid="report-submit-btn">
                {editingReport ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
